import { useHirerAuth } from '@seek/hirer-auth-react';
import { useTranslations } from '@vocab/react';
import { Box, ContentBlock, Heading, Stack } from 'braid-design-system';
import React, { useState } from 'react';

import { useTracking } from 'src/App/context/TrackingProvider.tsx';

import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import { ErrorSectionWithLayout } from '../../components/ErrorSection/ErrorSectionWithLayout';
import LargerBudgetPurchaseAlert from '../../components/LargerBudgetPurchaseAlert/LargerBudgetPurchaseAlert';
import { SystemBanner } from '../../components/SystemBanner/SystemBanner';
import { useAppContext } from '../../context/AppContext';
import {
  type CreateProductSetForBudgetsPurchaseForHirerMutation,
  type CreateProductSetForBudgetsPurchaseForVisitorMutation,
  useCreateProductSetForBudgetsPurchaseForHirerMutation,
  useCreateProductSetForBudgetsPurchaseForVisitorMutation,
  useHirerAccountQuery,
} from '../../generated/graphql-types';
import { useQueryAccountInfo } from '../../hooks/useQueryAccountInfo.ts';
import { AppError } from '../../utils/error';
import { getLogger } from '../../utils/logger/logger';

import translations from './.vocab';
import { AdBudgetValueProposition } from './components/AdBudgetValueProposition/AdBudgetValueProposition';
import { BudgetOptions } from './components/BudgetOptions/BudgetOptions';
import { CreditHoldAlert } from './components/CreditHoldAlert/CreditHoldAlert';
import { Faq } from './components/Faq/Faq';
import { LoadingBlock } from './components/Loading/Loading.tsx';
import { PriceCheckerPanel } from './components/PriceCheckerPanel/PriceCheckerPanel';
import { toError } from './error.ts';
import { createAdBudgetSelectionPageBaseEvent } from './eventTracking/createEvent';
import {
  type AdBudgetProductItemType,
  type BudgetOption,
  mapBudgetOption,
} from './mapBudgetOption';

import * as styles from './AdBudgetsPage.css';

interface Props {
  uuid: string;
}

type BudgetsProductSet =
  | CreateProductSetForBudgetsPurchaseForVisitorMutation['createProductSetForBudgetsPurchaseForVisitor']['productSet']
  | CreateProductSetForBudgetsPurchaseForHirerMutation['createProductSetForBudgetsPurchaseForHirer']['productSet'];

const prepareBudgetsForDisplay = (
  productSet: BudgetsProductSet,
  country: string,
) =>
  productSet.items
    .map((item) => mapBudgetOption(item as AdBudgetProductItemType, country))
    .sort(
      (budgetOptionA, budgetOptionB) =>
        budgetOptionA.budgetValue - budgetOptionB.budgetValue,
    );

export const AdBudgetsPage = ({ uuid }: Props) => {
  const [selectedBudgetType, setSelectedBudgetType] = useState('BrandedBudget');
  const { trackView, trackLink } = useTracking();
  const [, setErrorState] = useState();
  const { t } = useTranslations(translations);
  const { country, language, site } = useAppContext();
  const { authenticated } = useHirerAuth();
  const { accountInfo } = useQueryAccountInfo();
  const advertiserId = accountInfo?.currentAdvertiser?.advertiserId ?? '';

  const [
    createProductSetForBudgetsPurchaseForVisitor,
    {
      data: visitorData,
      loading: visitorLoading,
      called: createProductSetForVisitorMutationCalled,
    },
  ] = useCreateProductSetForBudgetsPurchaseForVisitorMutation({
    onError: (error) => {
      // https://www.developerway.com/posts/how-to-handle-errors-in-react
      setErrorState(() => {
        throw toError(error);
      });
    },
  });

  const [
    createProductSetForBudgetsPurchaseForHirer,
    {
      data: hirerData,
      loading: hirerLoading,
      called: createProductSetForHirerMutationCalled,
    },
  ] = useCreateProductSetForBudgetsPurchaseForHirerMutation({
    onError: (error) => {
      // https://www.developerway.com/posts/how-to-handle-errors-in-react
      setErrorState(() => {
        throw toError(error);
      });
    },
  });

  const {
    data: hirerAccountData,
    loading: hirerAccountDataLoading,
    error: hirerAccountError,
  } = useHirerAccountQuery({
    skip: !advertiserId,
  });

  const hirerBillingStatus = hirerAccountData?.hirerAccount?.billingStatus;

  const isOnCreditHold = hirerBillingStatus === 'ON_CREDIT_HOLD';

  const visitorProductSetId =
    visitorData?.createProductSetForBudgetsPurchaseForVisitor?.productSet.id;

  if (
    (!authenticated || isOnCreditHold) &&
    !visitorProductSetId &&
    !createProductSetForVisitorMutationCalled &&
    site
  ) {
    createProductSetForBudgetsPurchaseForVisitor({
      variables: { input: { visitorId: uuid } },
    });
  }

  const hirerProductSetId =
    hirerData?.createProductSetForBudgetsPurchaseForHirer?.productSet.id;

  if (
    authenticated &&
    hirerBillingStatus === 'OK' &&
    !hirerProductSetId &&
    !createProductSetForHirerMutationCalled
  ) {
    createProductSetForBudgetsPurchaseForHirer();
  }

  if (hirerAccountError) {
    const accountError = new AppError(
      'HIRER_ACCOUNT_QUERY_ERROR',
      'Error retrieving hirer account details',
    );
    getLogger().error(accountError.message, {
      activity: 'initialiseAdBudgetsInformation',
      error: accountError,
    });
  }

  if (
    hirerLoading ||
    visitorLoading ||
    hirerAccountDataLoading ||
    (!visitorData && !hirerData)
  ) {
    return <LoadingBlock />;
  }

  let budgetOptions: BudgetOption[] = [];
  let productSetId = '';

  if (visitorData) {
    budgetOptions = prepareBudgetsForDisplay(
      visitorData.createProductSetForBudgetsPurchaseForVisitor.productSet,
      country,
    );
    productSetId =
      visitorData.createProductSetForBudgetsPurchaseForVisitor.productSet.id;
  } else if (hirerData) {
    budgetOptions = prepareBudgetsForDisplay(
      hirerData.createProductSetForBudgetsPurchaseForHirer.productSet,
      country,
    );
    productSetId =
      hirerData.createProductSetForBudgetsPurchaseForHirer.productSet.id;
  } else {
    throw new AppError(
      'GET_AD_BUDGETS_ERROR',
      'Error retrieving product set id',
    );
  }

  const trackingBaseEvent = createAdBudgetSelectionPageBaseEvent({
    adBudgetType: selectedBudgetType,
    adBudgetsList: budgetOptions,
    ...(hirerAccountData &&
      hirerAccountData.hirerAccount && {
        pricingModel: hirerAccountData.hirerAccount.pricingModel,
      }),
    productSetId,
    currentPage: 'seek contracts',
  });

  trackView({
    eventName: 'seek_contract_displayed',
    data: {
      ...trackingBaseEvent,
    },
  });

  const priceCheckerOnClick = (eventName: string) => {
    if (!budgetOptions) {
      return;
    }

    trackLink({
      eventName,
      data: trackingBaseEvent,
    });
  };

  return (
    <ErrorBoundary errorDisplay={<ErrorSectionWithLayout />}>
      <Box background="surface">
        <SystemBanner context="ad_budget_product_page" />
        <ContentBlock>
          <Box paddingTop={['xlarge', 'xxlarge']}>
            <Stack space="xlarge" align="center">
              <Box
                marginX={{ tablet: 'gutter', mobile: 'gutter' }}
                className={styles.creditHoldAlert}
              >
                {hirerBillingStatus && isOnCreditHold && <CreditHoldAlert />}
              </Box>
              <Stack space="large" align="center">
                <Box paddingX="medium" paddingBottom="medium">
                  <Stack space="xlarge" align="center">
                    <Heading align="center" level="1">
                      {t('Pay upfront and get a discount...')}
                    </Heading>
                    <Box paddingX="medium" maxWidth="small">
                      <Heading level="3" weight="weak" align="center">
                        {t('Choose an Ad Budget and use...')}
                      </Heading>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  marginX={{ mobile: 'gutter' }}
                  className={styles.alertAndPriceCheckerPanel}
                >
                  <Stack space="gutter" align="center">
                    <BudgetOptions
                      budgetOptions={budgetOptions}
                      selectedBudgetType={selectedBudgetType}
                      setSelectedBudgetType={setSelectedBudgetType}
                      trackingBaseEvent={trackingBaseEvent}
                      productSetId={productSetId}
                      showBuyNowButton={!isOnCreditHold}
                    />
                    {!isOnCreditHold && <LargerBudgetPurchaseAlert />}
                    <PriceCheckerPanel
                      country={country}
                      language={language}
                      onClick={priceCheckerOnClick}
                    />
                  </Stack>
                </Box>
              </Stack>
              <AdBudgetValueProposition
                selectedBudgetType={selectedBudgetType}
              />
              <Faq
                selectedBudgetType={selectedBudgetType}
                trackingBaseEvent={trackingBaseEvent}
              />
            </Stack>
          </Box>
        </ContentBlock>
      </Box>
    </ErrorBoundary>
  );
};
